import MiniSearch from 'minisearch';
import { faqItems } from '../data/faq';
import type { Profile } from '../types/profile';
import { articles } from './content';
import { canViewProfileContent } from './permissions';
import { articlePath } from './routes';

interface SearchDocument {
  id: string;
  title: string;
  description: string;
  keywords: string;
  bodyText: string;
  category: string;
  profile: Profile;
  slug: string;
  kind: 'article' | 'faq';
  faqId: string;
}

export interface HelpSearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  profile: Profile;
  slug: string;
  kind: 'article' | 'faq';
  route: string;
  score: number;
}

const SYNONYMS: Record<string, string[]> = {
  travado: ['bloqueado', 'prerequisito'],
  bloqueado: ['travado', 'prerequisito'],
  curso: ['treinamento'],
  professor: ['mentor'],
  aluno: ['mentorado'],
  mentorado: ['aluno'],
  senha: ['login', 'acesso'],
  login: ['acesso', 'senha'],
};

function normalizeTerm(term: string) {
  return term
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function expandSearchQuery(query: string) {
  const terms = query.trim().split(/\s+/).filter(Boolean);
  const expanded = new Set<string>();

  for (const term of terms) {
    const normalized = normalizeTerm(term);
    expanded.add(normalized);
    for (const synonym of SYNONYMS[normalized] ?? []) expanded.add(synonym);
  }

  return [...expanded].join(' ');
}

const articleDocuments: SearchDocument[] = articles
  .filter((article) => article.meta.status === 'published')
  .map((article) => ({
    id: article.meta.id,
    title: article.meta.title,
    description: article.meta.description,
    keywords: article.meta.keywords.join(' '),
    bodyText: article.bodyText,
    category: article.meta.category,
    profile: article.meta.profile,
    slug: article.meta.slug,
    kind: 'article',
    faqId: '',
  }));

const faqDocuments: SearchDocument[] = faqItems.map((item) => ({
  id: item.id,
  title: item.question,
  description: item.answer,
  keywords: item.keywords.join(' '),
  bodyText: item.answer,
  category: 'faq',
  profile: item.profile,
  slug: '',
  kind: 'faq',
  faqId: item.id,
}));

const searchIndex = new MiniSearch<SearchDocument>({
  fields: ['title', 'keywords', 'description', 'bodyText', 'category'],
  storeFields: ['title', 'description', 'category', 'profile', 'slug', 'kind', 'faqId'],
  processTerm: normalizeTerm,
  searchOptions: {
    boost: { title: 6, keywords: 4, description: 2, category: 1.25 },
    prefix: true,
    fuzzy: 0.2,
  },
});

searchIndex.addAll([...articleDocuments, ...faqDocuments]);

export function searchArticles(query: string, profile: Profile): HelpSearchResult[] {
  const term = query.trim();
  if (!term) return [];

  return searchIndex
    .search(expandSearchQuery(term), {
      filter: (result) => canViewProfileContent(profile, result.profile as Profile),
    })
    .map((result) => {
      const kind = result.kind === 'faq' ? 'faq' : 'article';
      const category = String(result.category);
      const slug = String(result.slug);
      const faqId = String(result.faqId ?? '');
      return {
        id: String(result.id),
        title: String(result.title),
        description: String(result.description),
        category,
        profile: result.profile as Profile,
        slug,
        kind,
        route: kind === 'faq' ? `/faq#faq-${faqId}` : articlePath(profile, category, slug),
        score: result.score,
      };
    });
}
